import axios from 'axios';
import store from '../../redux/store';
import { jwtDecode } from 'jwt-decode';
import { logout, updateToken } from '../../redux/slices/authSlice';

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  const decoded = jwtDecode(token);
  const currentTime = Date.now() / 1000;

  return decoded.exp < currentTime; // Return true if expired
};

const baseURL = process.env.REACT_APP_API_URL;

// Create an Axios instance
const api = axios.create({
  baseURL: baseURL,
});
api.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.token?.access;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`; 
    }

    // Default Content-Type for JSON
    config.headers['Content-Type'] =
      config.headers['Content-Type'] || 'application/json';
    return config;
  },
  (error) => {
    console.log('api', error);
    Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const refreshToken = store.getState().auth?.token?.refresh;

    const errorDetail = error?.response?.data?.detail;
    const errorCode = error?.response?.data?.code;
    // Direct logout if user not found
    if (
      error?.response?.status === 401 &&
      (errorDetail === 'User not found' || errorCode === 'user_not_found')
    ) {
      store.dispatch(logout());
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // If the access token is expired (status 401) and we have a refresh token, try to refresh it
    if (
      error?.response?.status === 401 &&
      refreshToken &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (isTokenExpired(refreshToken)) {
        // Refresh token is also expired, log out the user
        store.dispatch(logout());
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Request new access token using refresh token
        const response = await axios.post(`${baseURL}/user/token/refresh`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data; // New access token from SimpleJWT
        store.dispatch(updateToken({ token: newAccessToken })); // Update Redux with new token

        // Retry the original request with the new access token
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest); // Retry request with new token
      } catch (err) {
        // Refresh token is invalid, log out the user
        store.dispatch(logout());
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// GET
export const get = (url, params = {}, config = {}) => {
  return api.get(url, { params, ...config });
};

// POST
export const post = (url, data = {}, config = {}) => {
  return api.post(url, data, config);
};

// PUT
export const put = (url, data = {}, config = {}) => {
  return api.put(url, data, config);
};

// PATCH
export const patch = (url, data = {}, config = {}) => {
  return api.patch(url, data, config);
};

// DELETE
export const remove = (url, config = {}) => {
  return api.delete(url, config);
};

// File Upload (multipart/form-data)
export const upload = (url, formData, config = {}) => {
  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config.headers,
    },
    ...config,
  });
};
